import type {
  EuropeHighlight,
  EuropeHighlightsCollection,
  HighlightActivity,
  HighlightOutbound,
  OutboundProvider,
} from "../types/europe-highlights";
import collection from "../data/europe-highlights.json";
import seedCompact from "../data/world-seed-highlights.json";
import adm1Regions from "../data/world-regions-index.json";
import raftingRiverPack from "../data/rafting-river-sections.js";
import { applyRaftingRiverPack } from "./apply-rafting-river-pack.js";
import { calculateDistance } from "./geo-utils";
import { getCountryHero } from "../data/country-heroes";
import { getHighlightCardImage } from "./region-card-visual";

const data = collection as EuropeHighlightsCollection;
const RIVER_PACK = raftingRiverPack as Record<string, any>;

/** Compact seed: country → [regionSlug, name, lat, lng][] */
type SeedCompact = {
  v: number;
  countries: Record<string, Array<[string, string, number, number]>>;
};

const seedData = seedCompact as SeedCompact;

export function highlightPath(slug: string): string {
  return `/highlights/${slug}/`;
}

function regionPagePath(country: string, regionSlug?: string): string {
  if (country && regionSlug && regionSlug !== "hub") {
    return `/${country}/${regionSlug}/`;
  }
  if (country) return `/${country}/`;
  return highlightPath("seed");
}

function expandSeedHighlights(): EuropeHighlight[] {
  const out: EuropeHighlight[] = [];
  for (const [country, rows] of Object.entries(seedData.countries || {})) {
    for (const [regionSlug, name, lat, lng] of rows) {
      const slug = `seed-${country}-${regionSlug}`;
      out.push({
        id: slug,
        slug,
        name,
        summary: `Orientation pin for ${name} — not a verified community point yet.`,
        country,
        region: name,
        activities: ["trail"],
        primaryType: "trail",
        features: ["Region hub", "Orientation", "Seed"],
        role: "orientation",
        verified: false,
        scale: "hotspot",
        // Prefer real country photo; never Mapbox satellite stills in galleries
        image: getCountryHero(country) || "",
        imageCredit: getCountryHero(country) ? "Country photo" : undefined,
        tier: "priority",
        priority: 2,
        source: "seed",
        latitude: lat,
        longitude: lng,
        path: regionPagePath(country, regionSlug),
      });
    }
  }
  return out;
}

function normalizeHighlight(
  feature: EuropeHighlightsCollection["features"][number]
): EuropeHighlight {
  const [longitude, latitude] = feature.geometry.coordinates;
  const props = feature.properties;
  const slug = props.slug || props.id;
  const base: EuropeHighlight = {
    ...props,
    slug,
    primaryType: props.primaryType || props.activities[0] || "mountain",
    features: props.features || [],
    facts: props.facts || [],
    role: props.role || "orientation",
    verified: props.verified === true,
    scale: props.scale || "feature",
    image: props.image,
    tier: props.tier || "iconic",
    priority: props.priority ?? (props.tier === "priority" ? 10 : 1),
    latitude,
    longitude,
    path: highlightPath(slug),
    riverLine: props.riverLine,
    sections: props.sections,
  };
  return applyRaftingRiverPack(base, RIVER_PACK) as EuropeHighlight;
}

/**
 * Curated orientation pins + world seed pins for empty countries.
 * Curated corpus also mirrored at data/europe-highlights.geojson.
 * Cached — expanding ~3k seeds on every call froze country pages (Italy).
 */
let allHighlightsCache: EuropeHighlight[] | null = null;
export function getEuropeHighlights(): EuropeHighlight[] {
  if (!allHighlightsCache) {
    const curated = data.features.map(normalizeHighlight);
    const curatedSlugs = new Set(curated.map((h) => h.slug));
    const seeds = expandSeedHighlights().filter((h) => !curatedSlugs.has(h.slug));
    allHighlightsCache = [...curated, ...seeds].sort(
      (a, b) => (b.priority || 0) - (a.priority || 0)
    );
  }
  return allHighlightsCache;
}

/** Curated pins only — no world seed hubs (use for activity browse / hot paths). */
let curatedOnlyCache: EuropeHighlight[] | null = null;
export function getCuratedEuropeHighlights(): EuropeHighlight[] {
  if (!curatedOnlyCache) {
    curatedOnlyCache = data.features
      .map(normalizeHighlight)
      .filter((h) => h.source !== "seed")
      .sort((a, b) => (b.priority || 0) - (a.priority || 0));
  }
  return curatedOnlyCache;
}

export function getScotlandHighlights(): EuropeHighlight[] {
  return getEuropeHighlights().filter((h) => h.country === "scotland");
}

export function getEuropeHighlightBySlug(slug: string): EuropeHighlight | undefined {
  return getEuropeHighlights().find((h) => h.slug === slug);
}

export function slugifyRegion(region?: string): string {
  return (region || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/ł/g, "l")
    .replace(/đ/g, "d")
    .replace(/ø/g, "o")
    .replace(/æ/g, "ae")
    .replace(/œ/g, "oe")
    .replace(/ß/g, "ss")
    .replace(/&/g, "and")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
}

export type HighlightRegion = {
  countrySlug: string;
  slug: string;
  name: string;
  lat: number;
  lng: number;
  count: number;
};

type Adm1Region = {
  countrySlug: string;
  slug: string;
  name: string;
  lat: number;
  lng: number;
};

const ADM1_REGIONS = adm1Regions as Adm1Region[];

export function normalizeRegionKey(slug?: string | null): string {
  return (slug || "").toLowerCase().replace(/_/g, "-");
}

/**
 * Informal curator labels → ADM1 slugs (country-specific).
 * Keep narrow: soft prefix matching is unsafe (west vs westfjords).
 */
const REGION_ALIASES: Record<string, Record<string, string>> = {
  iceland: {
    west: "western",
    western: "western",
    south: "southern",
    southern: "southern",
    highlands: "southern",
    highland: "southern",
    east: "austurland",
    eastern: "austurland",
    austurland: "austurland",
    reykjavik: "reykjavik",
    capital: "reykjavik",
    "southern-peninsula": "southern-peninsula",
    westfjords: "westfjords",
    northeastern: "northeastern",
    northwestern: "northwestern",
    north: "northeastern",
    northern: "northeastern",
  },
};

/**
 * Regions for a country: ADM1 map divisions merged with highlight-tagged regions.
 */
export function getRegionsForCountry(countrySlug: string): HighlightRegion[] {
  if (!countrySlug) return [];
  const bySlug = new Map<string, HighlightRegion>();

  for (const r of ADM1_REGIONS) {
    if (r.countrySlug !== countrySlug) continue;
    bySlug.set(r.slug, {
      countrySlug: r.countrySlug,
      slug: r.slug,
      name: r.name,
      lat: r.lat,
      lng: r.lng,
      count: 0,
    });
  }

  for (const r of getAllHighlightRegions()) {
    if (r.countrySlug !== countrySlug) continue;
    const existing = bySlug.get(r.slug);
    if (existing) {
      existing.count = r.count;
      // Prefer highlight display name when present
      if (r.name) existing.name = r.name;
    } else {
      bySlug.set(r.slug, { ...r });
    }
  }

  return [...bySlug.values()].sort((a, b) => a.name.localeCompare(b.name));
}

/**
 * All unique country+region pairs from the Europe highlight corpus.
 */
export function getAllHighlightRegions(): HighlightRegion[] {
  type Acc = {
    countrySlug: string;
    slug: string;
    name: string;
    sumLat: number;
    sumLng: number;
    count: number;
  };
  const byKey = new Map<string, Acc>();

  for (const h of getEuropeHighlights()) {
    const rawSlug = slugifyRegion(h.region);
    if (!h.country || !rawSlug) continue;
    const regionSlug =
      REGION_ALIASES[h.country]?.[normalizeRegionKey(rawSlug)] || rawSlug;
    const key = `${h.country}::${regionSlug}`;
    const existing = byKey.get(key);
    if (existing) {
      existing.sumLat += h.latitude;
      existing.sumLng += h.longitude;
      existing.count += 1;
    } else {
      byKey.set(key, {
        countrySlug: h.country,
        slug: regionSlug,
        name: h.region as string,
        sumLat: h.latitude,
        sumLng: h.longitude,
        count: 1,
      });
    }
  }

  return [...byKey.values()]
    .map((r) => ({
      countrySlug: r.countrySlug,
      slug: r.slug,
      name: r.name,
      lat: r.sumLat / r.count,
      lng: r.sumLng / r.count,
      count: r.count,
    }))
    .sort((a, b) => a.name.localeCompare(b.name));
}

/** Centroid lookup for a country region (ADM1 or highlight). */
export function getRegionCentroid(
  countrySlug: string,
  regionSlug: string
): { lat: number; lng: number; name: string } | null {
  const key = normalizeRegionKey(regionSlug);
  const match = getRegionsForCountry(countrySlug).find(
    (r) => normalizeRegionKey(r.slug) === key
  );
  if (!match) return null;
  return { lat: match.lat, lng: match.lng, name: match.name };
}

/**
 * Resolve a highlight/WP region label to a live /{country}/{region}/ path.
 */
export function resolveRegionPath(
  countrySlug?: string | null,
  regionNameOrSlug?: string | null
): { path: string; slug: string; name: string } | null {
  if (!countrySlug || !regionNameOrSlug) return null;
  const regions = getRegionsForCountry(countrySlug);
  const key = normalizeRegionKey(slugifyRegion(regionNameOrSlug));
  const rawKey = normalizeRegionKey(regionNameOrSlug);
  const match = regions.find((r) => {
    const slug = normalizeRegionKey(r.slug);
    const nameSlug = normalizeRegionKey(slugifyRegion(r.name));
    return (
      slug === key ||
      slug === rawKey ||
      nameSlug === key ||
      nameSlug === rawKey ||
      slug.includes(key) ||
      key.includes(slug.replace(/-voivodeship$/, "").replace(/-region$/, ""))
    );
  });
  if (match) {
    return {
      path: `/${countrySlug}/${match.slug}/`,
      slug: match.slug,
      name: match.name,
    };
  }
  if (key) {
    return {
      path: `/${countrySlug}/${key}/`,
      slug: key,
      name: regionNameOrSlug,
    };
  }
  return null;
}

/** Canonical ADM1 slug for a highlight’s region label within a country. */
export function resolveHighlightRegionSlug(
  countrySlug: string,
  regionLabel?: string | null
): string | null {
  if (!countrySlug || !regionLabel) return null;
  const raw = normalizeRegionKey(slugifyRegion(regionLabel));
  if (!raw) return null;

  const aliased = REGION_ALIASES[countrySlug]?.[raw];
  if (aliased) return aliased;

  const regions = getRegionsForCountry(countrySlug);
  const exact = regions.find((r) => normalizeRegionKey(r.slug) === raw);
  if (exact) return exact.slug;

  const byName = regions.find(
    (r) => normalizeRegionKey(slugifyRegion(r.name)) === raw
  );
  if (byName) return byName.slug;

  return raw;
}

/**
 * Highlights for a WP locality page (category) or synthetic region page.
 * Top-level country slug → all pins for that country.
 * Subcategory / region → pins whose region resolves to that ADM1 slug.
 */
const localityCache = new Map<string, EuropeHighlight[]>();

export function isRegionHubSeed(h: EuropeHighlight): boolean {
  return (
    h.source === "seed" &&
    (h.features || []).some((f) => /region hub/i.test(String(f)))
  );
}

export function getHighlightsForLocality(
  countrySlug: string,
  localitySlug?: string | null
): EuropeHighlight[] {
  if (!countrySlug) return [];
  const key = `${countrySlug}::${localitySlug || countrySlug}`;
  const cached = localityCache.get(key);
  if (cached) return cached;

  const inCountry = getEuropeHighlights().filter((h) => h.country === countrySlug);
  let result: EuropeHighlight[];
  if (!localitySlug || localitySlug === countrySlug) {
    // Country page: region-hub seeds duplicate RegionsList — exclude them
    result = inCountry.filter((h) => !isRegionHubSeed(h));
  } else {
    const target = normalizeRegionKey(localitySlug);
    result = inCountry.filter((h) => {
      if (isRegionHubSeed(h)) return false;
      const resolved = resolveHighlightRegionSlug(countrySlug, h.region);
      if (resolved && normalizeRegionKey(resolved) === target) return true;
      const regionSlug = normalizeRegionKey(slugifyRegion(h.region));
      return regionSlug === target;
    });
  }
  localityCache.set(key, result);
  return result;
}

function titleFromSlug(slug?: string | null): string {
  return (slug || "")
    .split("-")
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

function cardExcerptForHighlight(highlight: EuropeHighlight): string {
  const s = (highlight.summary || "").replace(/\s+/g, " ").trim();
  if (
    !s ||
    /^orientation pin for .+ — not a verified/i.test(s) ||
    /is on the map as an orientation pin/i.test(s) ||
    /^already on the orientation map around/i.test(s) ||
    /^orientation pins nearby include/i.test(s)
  ) {
    const country = titleFromSlug(highlight.country);
    const region = highlight.region ? `${highlight.region}, ` : "";
    return `${highlight.name} — map pin for ${region}${country}. Open for nearby outdoor days and bases.`;
  }
  return s;
}

/** Shape compatible with PostCard + CategoryArchive point grouping. */
export function highlightAsLocalityCard(highlight: EuropeHighlight) {
  const typeLabel =
    HIGHLIGHT_ACTIVITY_LABEL[highlight.primaryType] || highlight.primaryType;
  const countrySlug = (highlight.country || "").toLowerCase();
  const countryLabel = titleFromSlug(countrySlug);
  const regionSlug =
    resolveHighlightRegionSlug(highlight.country, highlight.region) ||
    slugifyRegion(highlight.region) ||
    highlight.slug;
  const imageSrc = getHighlightCardImage({
    country: highlight.country,
    regionSlug,
    image: highlight.image,
    slug: highlight.slug,
  });
  return {
    __typename: "EuropeHighlight",
    id: highlight.id,
    title: highlight.name,
    excerpt: cardExcerptForHighlight(highlight),
    // Absolute path — PostCard must not prefix a WP category
    uri: highlight.path || `/highlights/${highlight.slug}/`,
    featuredImage: imageSrc
      ? { node: { sourceUrl: imageSrc, altText: highlight.name } }
      : undefined,
    categories: {
      nodes: countrySlug
        ? [
            {
              id: countrySlug,
              databaseId: 0,
              name: countryLabel,
              slug: countrySlug,
              parentDatabaseId: 0,
            },
          ]
        : [],
    },
    tags: {
      nodes: [
        {
          id: highlight.primaryType,
          databaseId: 0,
          name: typeLabel,
          slug: highlight.primaryType,
          parentDatabaseId: 0,
        },
      ],
    },
    customAttributes: (() => {
      const attrs = (highlight.facts || []).map((f) => ({
        title: f.key,
        value: f.value,
      }));
      // Prefer useful orientation facts; de-emphasize Role/Scale labels on cards
      const rank = (t: string) => {
        const k = t.toLowerCase();
        if (highlight.primaryType === "climbing") {
          if (k === "routes") return 0;
          if (k === "grades") return 1;
          if (k === "height") return 2;
        }
        if (k === "day trips" || k === "focus" || k === "season") return 1;
        if (k === "access" || k === "base") return 2;
        if (k === "role" || k === "scale") return 8;
        return 5;
      };
      return [...attrs]
        .filter(
          (a) =>
            a.title.toLowerCase() !== "difficulty" ||
            !attrs.some((x) => x.title.toLowerCase() === "grades")
        )
        .sort((a, b) => rank(a.title) - rank(b.title))
        .slice(0, 3);
    })(),
    pointType: {
      nodes: [{ name: typeLabel, slug: highlight.primaryType }],
    },
    primaryType: highlight.primaryType,
    scale: highlight.scale || "feature",
    features: highlight.features || [],
    location: {
      mapLatitude: String(highlight.latitude),
      mapLongitude: String(highlight.longitude),
    },
  };
}

/**
 * Orientation pins within radiusKm of a place (for post “points around” sections).
 */
export function getHighlightsNear(
  lat: number,
  lng: number,
  radiusKm = 80,
  limit = 24,
  opts?: {
    /** Drop city / town logistics hubs (they’re often mistagged as trail). */
    excludeCityBases?: boolean;
    /** Prefer these activities when ranking (still includes others to fill). */
    preferActivities?: HighlightActivity[];
    /** Skip this highlight slug (usually the current page). */
    excludeSlug?: string;
  }
): EuropeHighlight[] {
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return [];
  const prefer = new Set(
    (opts?.preferActivities || []).map((a) => a.toLowerCase())
  );
  return getEuropeHighlights()
    .filter((h) => {
      if (opts?.excludeSlug && h.slug === opts.excludeSlug) return false;
      if (opts?.excludeCityBases && isLogisticsHubHighlight(h)) return false;
      return true;
    })
    .map((h) => {
      const distance = calculateDistance(lat, lng, h.latitude, h.longitude);
      const tags = [h.primaryType, ...(h.activities || [])].map((t) =>
        (t || "").toLowerCase()
      );
      const preferred = prefer.size
        ? tags.some((t) => prefer.has(t))
          ? 0
          : 1
        : 0;
      return { highlight: h, distance, preferred };
    })
    .filter((x) => x.distance <= radiusKm)
    .sort(
      (a, b) =>
        a.preferred - b.preferred ||
        a.distance - b.distance ||
        (b.highlight.priority || 0) - (a.highlight.priority || 0)
    )
    .slice(0, limit)
    .map((x) => x.highlight);
}

/** City / town logistics hubs — not trails or activity features. */
export function isLogisticsHubHighlight(h: {
  features?: string[];
  name?: string;
  scale?: string;
}): boolean {
  return (h.features || []).some((f) =>
    /city\s*base|town\s*hub|city\s*hub|urban\s*base|logistics/i.test(String(f))
  );
}

/** Map activity tag → static icon filename (under /images/icons/). */
export const HIGHLIGHT_ACTIVITY_ICON: Record<HighlightActivity, string> = {
  climbing: "climbing",
  rafting: "lake",
  trail: "hiking",
  paragliding: "viewpoint",
  ferrata: "via-ferrata",
  forest: "nature-reserve",
  mountain: "mountains",
  lake: "lake",
  camping: "camping",
};

export const HIGHLIGHT_ACTIVITY_LABEL: Record<HighlightActivity, string> = {
  climbing: "Climbing",
  rafting: "Rafting",
  trail: "Trail",
  paragliding: "Paragliding",
  ferrata: "Via ferrata",
  forest: "Forest",
  mountain: "Mountain",
  lake: "Lake",
  camping: "Camp / stay",
};

export const HIGHLIGHT_ACTIVITIES: HighlightActivity[] = [
  "climbing",
  "rafting",
  "trail",
  "paragliding",
  "ferrata",
  "forest",
  "mountain",
  "lake",
  "camping",
];

/**
 * Curated orientation pins matching any of the given activities (excludes seed).
 * Excludes city/town logistics hubs — those are bases, not activity features.
 */
export function getHighlightsByActivities(
  activities: HighlightActivity[],
  opts?: { includeCityBases?: boolean }
): EuropeHighlight[] {
  const wanted = new Set(
    (activities || []).filter(Boolean).map((a) => a.toLowerCase())
  );
  if (!wanted.size) return [];
  return getCuratedEuropeHighlights().filter((h) => {
    const tags = [
      h.primaryType,
      ...(h.activities || []),
    ].map((t) => (t || "").toLowerCase());
    if (!tags.some((t) => wanted.has(t))) return false;
    if (!opts?.includeCityBases && isLogisticsHubHighlight(h)) return false;
    return true;
  });
}

export type HighlightCountryGroup = {
  countrySlug: string;
  highlights: EuropeHighlight[];
};

/** Group curated highlights by country (A–Z), pins by priority then name. */
export function groupHighlightsByCountry(
  highlights: EuropeHighlight[]
): HighlightCountryGroup[] {
  const byCountry = new Map<string, EuropeHighlight[]>();
  for (const h of highlights) {
    const c = h.country || "unknown";
    if (!byCountry.has(c)) byCountry.set(c, []);
    byCountry.get(c)!.push(h);
  }
  return [...byCountry.entries()]
    .map(([countrySlug, list]) => ({
      countrySlug,
      highlights: list
        .slice()
        .sort(
          (a, b) =>
            (b.priority || 0) - (a.priority || 0) ||
            a.name.localeCompare(b.name)
        ),
    }))
    .sort((a, b) => a.countrySlug.localeCompare(b.countrySlug));
}

/** Map framing from a set of pins (bbox center + coarse zoom). */
export function getHighlightsMapFrame(
  highlights: EuropeHighlight[],
  fallback = { lat: 48, lng: 10, zoom: 4 }
): { lat: number; lng: number; zoom: number } {
  if (!highlights.length) return fallback;
  let minLat = Infinity;
  let maxLat = -Infinity;
  let minLng = Infinity;
  let maxLng = -Infinity;
  for (const h of highlights) {
    if (!Number.isFinite(h.latitude) || !Number.isFinite(h.longitude)) continue;
    minLat = Math.min(minLat, h.latitude);
    maxLat = Math.max(maxLat, h.latitude);
    minLng = Math.min(minLng, h.longitude);
    maxLng = Math.max(maxLng, h.longitude);
  }
  if (!Number.isFinite(minLat)) return fallback;
  const lat = (minLat + maxLat) / 2;
  const lng = (minLng + maxLng) / 2;
  const span = Math.max(maxLat - minLat, maxLng - minLng);
  let zoom = 4;
  if (span < 0.3) zoom = 10;
  else if (span < 0.8) zoom = 9;
  else if (span < 2) zoom = 8;
  else if (span < 5) zoom = 7;
  else if (span < 12) zoom = 6;
  else if (span < 25) zoom = 5;
  return { lat, lng, zoom };
}

/** Frame a single highlight using river line + section endpoints when present. */
export function getHighlightDetailMapFrame(
  highlight: EuropeHighlight,
  fallbackZoom = 10
): { lat: number; lng: number; zoom: number; sectionId?: null } {
  const lats: number[] = [];
  const lngs: number[] = [];
  if (Number.isFinite(highlight.latitude) && Number.isFinite(highlight.longitude)) {
    lats.push(highlight.latitude);
    lngs.push(highlight.longitude);
  }
  for (const [lng, lat] of highlight.riverLine || []) {
    if (Number.isFinite(lat) && Number.isFinite(lng)) {
      lats.push(lat);
      lngs.push(lng);
    }
  }
  for (const s of highlight.sections || []) {
    lats.push(s.start.lat, s.end.lat);
    lngs.push(s.start.lng, s.end.lng);
  }
  if (!lats.length) {
    return {
      lat: highlight.latitude,
      lng: highlight.longitude,
      zoom: fallbackZoom,
      sectionId: null,
    };
  }
  const minLat = Math.min(...lats);
  const maxLat = Math.max(...lats);
  const minLng = Math.min(...lngs);
  const maxLng = Math.max(...lngs);
  const span = Math.max(maxLat - minLat, maxLng - minLng, 0.02);
  let zoom = fallbackZoom;
  if (span < 0.08) zoom = 12;
  else if (span < 0.2) zoom = 11;
  else if (span < 0.45) zoom = 10;
  else if (span < 1) zoom = 9;
  else zoom = 8;
  return {
    lat: (minLat + maxLat) / 2,
    lng: (minLng + maxLng) / 2,
    zoom,
    sectionId: null,
  };
}

/** Frame one rafting section (put-in → take-out). */
export function getSectionMapFrame(section: {
  start: { lat: number; lng: number };
  end: { lat: number; lng: number };
  id?: string;
}): { lat: number; lng: number; zoom: number; sectionId?: string } {
  const lat = (section.start.lat + section.end.lat) / 2;
  const lng = (section.start.lng + section.end.lng) / 2;
  const span = Math.max(
    Math.abs(section.start.lat - section.end.lat),
    Math.abs(section.start.lng - section.end.lng),
    0.02
  );
  let zoom = 12;
  if (span > 0.35) zoom = 9;
  else if (span > 0.18) zoom = 10;
  else if (span > 0.08) zoom = 11;
  return { lat, lng, zoom, sectionId: section.id };
}

export const OUTBOUND_PROVIDER_LABEL: Record<OutboundProvider, string> = {
  thecrag: "The Crag",
  viaferrata: "Via Ferrata",
  park4night: "Park4Night",
  other: "More info",
};

export function primaryHighlightIcon(activities: HighlightActivity[]): string {
  const primary = activities[0] || "mountain";
  return HIGHLIGHT_ACTIVITY_ICON[primary] || "landmark";
}

/** Prefer structured outbound; fall back to legacy url. */
export function resolveOutbound(highlight: EuropeHighlight): HighlightOutbound | null {
  if (highlight.outbound?.url) {
    return highlight.outbound;
  }
  if (highlight.url) {
    return {
      provider: "other",
      label: "More info",
      url: highlight.url,
    };
  }
  return null;
}

export type { HighlightActivity, EuropeHighlight };
